import { useEffect, useState } from 'react';
import { apiClient } from '../services/api';

export interface CrmLeadStage { value: string; label: string }
export interface CrmLeadStagesState {
    stages: CrmLeadStage[];
    fallback: boolean;
    reason?: string;
    loading: boolean;
    error: string | null;
}

// Module-scope cache shared across all consumers; backend mirrors the 5-min TTL.
let _cache: { stages: CrmLeadStage[]; fallback: boolean; reason?: string; expires: number } | null = null;

export function useCrmLeadStages(): CrmLeadStagesState {
    const [state, setState] = useState<CrmLeadStagesState>({
        stages: _cache?.stages ?? [],
        fallback: _cache?.fallback ?? false,
        reason: _cache?.reason,
        // Stale-while-revalidate: if a cache object exists, don't show a spinner
        // while the background refresh runs. Intentional UX — see scope doc.
        loading: !_cache,
        error: null,
    });

    useEffect(() => {
        if (_cache && _cache.expires > Date.now()) return;
        let cancelled = false;
        (async () => {
            try {
                const resp = await apiClient.get('/crm/lead-stages');
                if (cancelled) return;
                _cache = {
                    stages: resp.data.stages || [],
                    fallback: !!resp.data.fallback,
                    reason: resp.data.reason,
                    expires: Date.now() + 5 * 60 * 1000,
                };
                setState(s => ({
                    ...s,
                    stages: _cache!.stages,
                    fallback: _cache!.fallback,
                    reason: _cache!.reason,
                    loading: false,
                }));
            } catch (err: any) {
                if (cancelled) return;
                setState(s => ({ ...s, loading: false, error: err?.message ?? 'failed' }));
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return state;
}
